import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { CustomNavigationHeader } from '@sg/garnish';

import { type LocationSearchService } from '../../machine';
import { LocationSearchTypePicker } from '../LocationSearchTypePicker';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchHeader = (props: LocationSearchHeaderProps) => {
  const { locationSearchActorRef, shouldApplySafeAreaInsets = true, withBackground = true } = props;

  const { formatMessage } = useIntl();
  const router = useRouter();

  const canGoBackToPreviousScreen = router.canGoBack() ?? false;

  // ─────────────────────────────────────────────────────────────────────

  const withSafeAreaInsets =
    (shouldApplySafeAreaInsets && Platform.OS === 'ios') || Platform.OS === 'android';

  // ─────────────────────────────────────────────────────────────────────

  const handleGoBack = useCallback(() => {
    if (canGoBackToPreviousScreen) {
      router.back();
      return;
    }

    router.navigate('/');
  }, [canGoBackToPreviousScreen, router]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomNavigationHeader.Container
      transition="gradient"
      safeAreaEdges={withSafeAreaInsets ? ['top'] : []}
      withBackground={withBackground}
    >
      {canGoBackToPreviousScreen ? (
        <CustomNavigationHeader.Button.Left
          name="IconArrowLeft"
          onPress={handleGoBack}
          accessibilityLabel={formatMessage(messages.goBackButtonA11yLabel)}
        />
      ) : null}

      <LocationSearchTypePicker locationSearchActorRef={locationSearchActorRef} />

      <CustomNavigationHeader.Button.Right
        name="IconClose"
        onPress={handleGoBack}
        accessibilityLabel={formatMessage(messages.closeButtonA11yLabel)}
      />
    </CustomNavigationHeader.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  goBackButtonA11yLabel: {
    defaultMessage: 'Go back',
    description: 'Location Search | Navigation Header | Back button | a11y label',
  },
  closeButtonA11yLabel: {
    defaultMessage: 'Close',
    description: 'Location Search | Navigation Header | Close button | a11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchHeaderProps = {
  locationSearchActorRef: LocationSearchService;
  shouldApplySafeAreaInsets?: boolean;
  withBackground?: boolean;
};
